import { useEffect, useState } from 'react'
import { Link, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { InteractiveHoverButton } from '#app/components/ui/interactive-hover-button.tsx'
import { cn } from '#app/utils/misc.tsx'

// Default contact info for the footer (used when no admin settings exist)
const defaultContactInfo = {
	phone: 'T. 030 123 25 234',
	email: 'info@igniteidentity.nl',
	company: 'Ignite Identity B.V.',
	postalAddress: 'Postadres: Damraklaan 8, 3544 PJ Utrecht',
	visitAddress: 'Bezoekadres: Europalaan 2B, Utrecht',
}

// Default footer sections if no dynamic data exists
const defaultSections = [
	{
		id: 'diensten',
		title: 'DIENSTEN',
		links: [
			{ label: 'Strategie', url: '/strategie' },
			{ label: 'Merkidentiteit', url: '/merkidentiteit' },
			{ label: 'Communicatie advies', url: '/communicatie-advies' },
			{ label: 'Branding', url: '/branding' },
			{ label: 'Interne communicatie', url: '/interne-communicatie' },
			{ label: 'Audio identity', url: '/audio-identity' },
		],
	},
	{
		id: 'sectoren',
		title: 'SECTOREN',
		links: [
			{ label: 'Onderwijs', url: '/sectoren/onderwijs' },
			{ label: 'Energie', url: '/sectoren/energie' },
			{ label: 'MVO', url: '/sectoren/mvo' },
			{ label: 'Zorg', url: '/sectoren/zorg' },
			{ label: 'Overheid', url: '/sectoren/overheid' },
			{ label: 'B2B', url: '/sectoren/b2b' },
		],
	},
	{
		id: 'volgen',
		title: 'VOLGEN',
		links: [
			{ label: 'Instagram', url: 'https://instagram.com/igniteidentity', isExternal: true },
			{ label: 'LinkedIn', url: 'https://linkedin.com/company/igniteidentity', isExternal: true },
			{ label: 'YouTube', url: 'https://youtube.com/@igniteidentity', isExternal: true },
		],
	},
	{
		id: 'voorwaarden',
		title: 'VOORWAARDEN',
		links: [
			{ label: 'BNO', url: '/bno-voorwaarden' },
			{ label: 'AVG', url: '/privacy' },
		],
	},
	{
		id: 'werken-bij',
		title: 'WERKEN BIJ',
		links: [
			{ label: 'Vacatures', url: '/vacatures' },
		],
	},
]

interface FooterSection {
	id: string
	title: string
	links: Array<{
		id: string
		label: string
		url: string | null
		pageId: string | null
		isExternal: boolean
		page: { slug: string; pageType: string } | null
	}>
}

interface FooterContactInfo {
	phone: string | null
	email: string | null
	company: string | null
	postalAddress: string | null
	visitAddress: string | null
}

interface FooterProps {
	sections?: FooterSection[]
	contactInfo?: FooterContactInfo
}

export function Footer({ sections, contactInfo }: FooterProps) {
	const fetcher = useFetcher()
	const [email, setEmail] = useState('')
	const [showSuccess, setShowSuccess] = useState(false)
	const isSubmitting = fetcher.state === 'submitting'

	// Handle successful submission
	useEffect(() => {
		if (fetcher.data?.success && !isSubmitting) {
			setShowSuccess(true)
			setEmail('')
			// Hide success message after 5 seconds
			const timer = setTimeout(() => {
				setShowSuccess(false)
			}, 5000)
			return () => clearTimeout(timer)
		}
	}, [fetcher.data, isSubmitting])

	// Use dynamic sections if provided, otherwise fall back to defaults
	const hasDynamicSections = sections && sections.length > 0

	return (
		<footer className="relative bg-[#231a1a] text-[#fcf8eb] pt-20 pb-8">
			<div className="container mx-auto px-6 lg:px-12">
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-8 mb-12">
					{/* Contact Section - Uses admin settings with fallback to defaults */}
					<div className="col-span-1">
						<h3 className="font-bold text-xl mb-4 tracking-wider">CONTACT</h3>
						<div className="text-sm opacity-90 leading-relaxed">
							<div>
								<p>{contactInfo?.phone || defaultContactInfo.phone}</p>
								<p>{contactInfo?.email || defaultContactInfo.email}</p>
							</div>
							<div className="mt-4">
								<p>{contactInfo?.company || defaultContactInfo.company}</p>
							</div>
							<div className="mt-4">
								{(contactInfo?.postalAddress || defaultContactInfo.postalAddress)
									.split('\n')
									.map((line, i) => (
										<p key={i}>{line}</p>
									))}
							</div>
							{(contactInfo?.visitAddress || defaultContactInfo.visitAddress) && (
								<div className="mt-4">
									{(contactInfo?.visitAddress || defaultContactInfo.visitAddress)!
										.split('\n')
										.map((line, i) => (
											<p key={i}>{line}</p>
										))}
								</div>
							)}
						</div>
					</div>

					{/* Dynamic or Default Sections */}
					{hasDynamicSections ? (
						sections.map((section) => (
							<div key={section.id} className="col-span-1">
								<h3 className="font-bold text-xl mb-4 tracking-wider">
									{section.title}
								</h3>
								<div className="flex flex-col gap-1 text-sm leading-snug">
									{section.links.map((link) => {
										const href = link.page
											? link.page.pageType === 'service'
												? `/diensten/${link.page.slug.replace(/^diensten-/, '')}`
												: `/${link.page.slug}`
											: link.url || '#'
										const isExternal = link.isExternal || (link.url && link.url.startsWith('http'))

										if (isExternal) {
											return (
												<a
													key={link.id}
													href={href}
													target="_blank"
													rel="noopener noreferrer"
													className="hover:text-[#ff8500] transition-colors opacity-90 hover:opacity-100"
												>
													{link.label}
												</a>
											)
										}

										return (
											<Link
												key={link.id}
												to={href}
												className="hover:text-[#ff8500] transition-colors opacity-90 hover:opacity-100"
											>
												{link.label}
											</Link>
										)
									})}
								</div>
							</div>
						))
					) : (
						// Fall back to default sections
						defaultSections.map((section) => (
							<div key={section.id} className="col-span-1">
								<h3 className="font-bold text-xl mb-4 tracking-wider">
									{section.title}
								</h3>
								<div className="flex flex-col gap-1 text-sm leading-snug">
									{section.links.map((link, idx) => {
										const linkObj = link as { label: string; url: string; isExternal?: boolean }
										if (linkObj.isExternal) {
											return (
												<a
													key={idx}
													href={link.url}
													target="_blank"
													rel="noopener noreferrer"
													className="hover:text-[#ff8500] transition-colors opacity-90 hover:opacity-100"
												>
													{link.label}
												</a>
											)
										}

										return (
											<Link
												key={idx}
												to={link.url}
												className="hover:text-[#ff8500] transition-colors opacity-90 hover:opacity-100"
											>
												{link.label}
											</Link>
										)
									})}
								</div>
							</div>
						))
					)}
				</div>

				{/* Newsletter and Copyright Section */}
				<div className="pt-8 pb-8">
					<div className="flex flex-col lg:flex-row items-center justify-between gap-6">
						{/* Copyright - Left aligned */}
						<div className="text-[#ff8500] font-medium order-2 lg:order-1">
							© Ignite Identity
						</div>

						{/* Newsletter Form - Right aligned */}
						<div className="order-1 lg:order-2">
							{showSuccess ? (
								<div className="animate-fadeIn">
									<div className="bg-[#ff8500]/10 border border-[#ff8500] rounded-full px-6 py-3 text-center">
										<p className="text-[#ff8500] text-sm font-medium">
											Bedankt voor je inschrijving! Check je email om te bevestigen.
										</p>
									</div>
								</div>
							) : (
								<div className="flex flex-col sm:flex-row items-center gap-4">
									{/* Newsletter Label */}
									<p className="text-xl font-bold text-[#fcf8eb] whitespace-nowrap tracking-wide">
										Ontvang de nieuwsbrief
									</p>

									{/* Form */}
									<fetcher.Form
										method="post"
										action="/resources/newsletter/subscribe"
										className="flex items-center gap-0"
									>
										<HoneypotInputs />
										<Input
											type="email"
											name="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											placeholder="je@email.nl"
											required
											className={cn(
												"w-[240px] sm:w-[300px] h-12 bg-[#3a2e2e] text-[#fcf8eb]",
												"placeholder:text-[#fcf8eb]/60 rounded-l-full rounded-r-none px-6 text-base",
												"border border-[#5a4a4a] border-r-0 focus:ring-2 focus:ring-[#ff8500]/50 focus:border-[#ff8500]",
												"disabled:opacity-50"
											)}
											disabled={isSubmitting}
										/>
										<InteractiveHoverButton
											type="submit"
											disabled={isSubmitting}
											className="h-12 px-8 whitespace-nowrap rounded-l-none text-base"
										>
											{isSubmitting ? (
												<>
													<Icon
														name="update"
														className="h-4 w-4 animate-spin mr-2"
													/>
													Verzenden...
												</>
											) : (
												'Verstuur'
											)}
										</InteractiveHoverButton>
									</fetcher.Form>
								</div>
							)}
							{fetcher.data?.error && (
								<p className="mt-2 text-sm text-red-400 text-center">
									{fetcher.data.error}
								</p>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Add smooth fade-in animation */}
			<style>{`
				@keyframes fadeIn {
					from {
						opacity: 0;
						transform: translateY(10px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				.animate-fadeIn {
					animation: fadeIn 0.3s ease-out forwards;
				}
			`}</style>
		</footer>
	)
}
